// Diagnosis Service for managing diagnosis reports with Firebase Firestore
import { db } from '../../firebase';
import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    onSnapshot
} from 'firebase/firestore';

// ========================================
// DIAGNOSIS REPORTS - FIRESTORE INTEGRATION
// ========================================

/**
 * Save diagnosis report to Firestore
 * @param {Object} reportData - Complete diagnosis report data
 * @returns {Promise<string>} - Report ID
 */
export const saveDiagnosisReport = async (reportData) => {
    try {
        const reportRef = doc(db, 'diagnosisReports', reportData.reportId);

        const dataToSave = {
            ...reportData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: 'completed',
            financialHealth: {
                savingsRate: ((reportData.expenses.surplus / reportData.salary.totalIncome) * 100).toFixed(1),
                score: calculateHealthScore(reportData.expenses.surplus, reportData.salary.totalIncome),
                recommendations: generateRecommendations(reportData)
            }
        };

        await setDoc(reportRef, dataToSave);
        console.log('Diagnosis report saved successfully:', reportData.reportId);
        return reportData.reportId;
    } catch (error) {
        console.error('Error saving diagnosis report:', error);
        throw error;
    }
};

/**
 * Save draft diagnosis report to Firestore
 * @param {string} reportId - Unique report ID
 * @param {Object} currentData - Current state of form data
 * @param {string} userId - User ID if logged in
 * @returns {Promise<string>} - Report ID
 */
export const saveDraftReport = async (reportId, currentData, userId = null) => {
    try {
        const reportRef = doc(db, 'diagnosisReports', reportId);
        
        const dataToSave = {
            reportId,
            userId: userId || null,
            personalInfo: currentData.personalInfo || {},
            businessDetails: currentData.businessDetails || {},
            businessExpenses: currentData.businessExpenses || {},
            businessInvestment: currentData.businessInvestment || {},
            salaryIncome: currentData.salaryIncome || {},
            salaryExpenses: currentData.salaryExpenses || {},
            salaryInvestment: currentData.salaryInvestment || {},
            jointSalary: currentData.jointSalary || {},
            salary: currentData.salary || {},
            expenses: currentData.expenses || {},
            landGold: currentData.landGold || {},
            taxDetails: currentData.taxDetails || {},
            updatedAt: serverTimestamp(),
            status: 'incomplete',
            payment: {
                status: 'unpaid'
            }
        };

        const docSnap = await getDoc(reportRef);
        if (!docSnap.exists()) {
            dataToSave.createdAt = serverTimestamp();
        }

        await setDoc(reportRef, dataToSave, { merge: true });
        console.log('Draft report saved successfully:', reportId);
        return reportId;
    } catch (error) {
        console.error('Error saving draft report:', error);
    }
};

/**
 * Get incomplete reports (Admin only)
 * @returns {Promise<Array>} - Array of all incomplete reports
 */
export const getIncompleteReports = async () => {
    try {
        const reportsRef = collection(db, 'diagnosisReports');
        const q = query(reportsRef, where('status', '==', 'incomplete'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        const reports = [];
        querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            // Exclude demo / admin entries
            if (data.personalInfo?.email !== 'admin@yatharthaweb.com') {
                reports.push({
                    id: docSnap.id,
                    ...data,
                    createdAt: data.createdAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate()
                });
            }
        });

        return reports;
    } catch (error) {
        console.error('Error fetching incomplete reports, using fallback filter:', error);
        try {
            const all = await getAllDiagnosisReports();
            return all.filter(r => r.status === 'incomplete' && r.personalInfo?.email !== 'admin@yatharthaweb.com');
        } catch (err) {
            console.error('Error in fallback incomplete fetch:', err);
            throw error;
        }
    }
};

/**
 * Subscribe to incomplete reports in real-time (Admin only)
 * @param {Function} callback - Callback function with reports array
 * @returns {Function} - Unsubscribe function
 */
export const subscribeIncompleteReports = (callback) => {
    try {
        const reportsRef = collection(db, 'diagnosisReports');
        const q = query(reportsRef, where('status', '==', 'incomplete'), orderBy('createdAt', 'desc'));
        
        return onSnapshot(q, (querySnapshot) => {
            const reports = [];
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data.personalInfo?.email !== 'admin@yatharthaweb.com') {
                    reports.push({
                        id: docSnap.id,
                        ...data,
                        createdAt: data.createdAt?.toDate(),
                        updatedAt: data.updatedAt?.toDate()
                    });
                }
            });
            callback(reports);
        }, (error) => {
            console.error('Error in real-time incomplete subscription, falling back:', error);
            getIncompleteReports().then(callback).catch(console.error);
        });
    } catch (error) {
        console.error('Failed to subscribe to incomplete reports:', error);
    }
};

/**
 * Get all diagnosis reports (Admin only)
 * @returns {Promise<Array>} - Array of all diagnosis reports
 */
export const getAllDiagnosisReports = async () => {
    try {
        const reportsRef = collection(db, 'diagnosisReports');
        const q = query(reportsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        const reports = [];
        querySnapshot.forEach((docSnap) => {
            reports.push({
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate(),
                updatedAt: docSnap.data().updatedAt?.toDate()
            });
        });

        return reports;
    } catch (error) {
        console.error('Error fetching diagnosis reports:', error);
        throw error;
    }
};

/**
 * Get diagnosis report by ID
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} - Diagnosis report data
 */
export const getDiagnosisReportById = async (reportId) => {
    try {
        const reportRef = doc(db, 'diagnosisReports', reportId);
        const reportSnap = await getDoc(reportRef);

        if (reportSnap.exists()) {
            return {
                id: reportSnap.id,
                ...reportSnap.data(),
                createdAt: reportSnap.data().createdAt?.toDate(),
                updatedAt: reportSnap.data().updatedAt?.toDate()
            };
        } else {
            throw new Error('Report not found');
        }
    } catch (error) {
        console.error('Error fetching diagnosis report:', error);
        throw error;
    }
};

/**
 * Update report status
 * @param {string} reportId - Report ID
 * @param {string} status - New status
 * @returns {Promise<void>}
 */
export const updateReportStatus = async (reportId, status) => {
    try {
        const reportRef = doc(db, 'diagnosisReports', reportId);
        await updateDoc(reportRef, {
            status,
            updatedAt: serverTimestamp()
        });
        console.log('Report status updated:', reportId, status);
    } catch (error) {
        console.error('Error updating report status:', error);
        throw error;
    }
};

// ...existing code...
export const deleteReport = async (reportId) => {
    try {
        const reportRef = doc(db, 'diagnosisReports', reportId);
        await deleteDoc(reportRef);
        console.log('Report deleted:', reportId);
    } catch (error) {
        console.error('Error deleting report:', error);
        throw error;
    }
};

// ...existing code...
export const getLatestReportByEmail = async (email, userId = null) => {
    try {
        const reportsRef = collection(db, 'diagnosisReports');
        let reports = [];

        // 1. Try to search by userId first
        if (userId) {
            const qUser = query(reportsRef, where('userId', '==', userId));
            const snapUser = await getDocs(qUser);
            snapUser.forEach(d => reports.push({ id: d.id, ...d.data() }));
        } 
        
        // 2. If no reports found by userId (legacy reports), or no userId provided, search by email
        if (reports.length === 0 && email) {
            const qEmail = query(reportsRef, where('personalInfo.email', '==', email));
            const snapEmail = await getDocs(qEmail);
            snapEmail.forEach(d => reports.push({ id: d.id, ...d.data() }));

            // Also try lowercase email
            if (email.toLowerCase() !== email) {
                const qEmailLower = query(reportsRef, where('personalInfo.email', '==', email.toLowerCase()));
                const snapEmailLower = await getDocs(qEmailLower);
                snapEmailLower.forEach(d => {
                    if (!reports.find(r => r.id === d.id)) {
                        reports.push({ id: d.id, ...d.data() });
                    }
                });
            }
        }

        if (reports.length > 0) {
            // Sort by createdAt desc
            const sortedReports = reports.map(r => ({
                ...r,
                createdAt: r.createdAt?.toDate() || new Date(0)
            })).sort((a, b) => b.createdAt - a.createdAt);

            return sortedReports[0];
        }
        return null;
    } catch (error) {
        console.error('Error fetching latest report:', error);
        return null;
    }
};

/**
 * Get reports by date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Array>} - Filtered reports
 */
export const getReportsByDateRange = async (startDate, endDate) => {
    try {
        const reportsRef = collection(db, 'diagnosisReports');
        const q = query(
            reportsRef,
            where('createdAt', '>=', startDate),
            where('createdAt', '<=', endDate),
            orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);

        const reports = [];
        querySnapshot.forEach((docSnap) => {
            reports.push({
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate()
            });
        });

        return reports;
    } catch (error) {
        console.error('Error fetching reports by date range:', error);
        throw error;
    }
};

/**
 * Get reports statistics
 * @returns {Promise<Object>} - Statistics object
 */
export const getReportsStatistics = async () => {
    try {
        const allReports = await getAllDiagnosisReports();
        const reports = allReports.filter(r => r.status !== 'incomplete');

        // Calculate real revenue ignoring DEMO reports
        const totalRevenue = reports.reduce((sum, r) => {
            const isDemo = r.payment?.paymentId?.startsWith('DEMO_');
            return isDemo ? sum : sum + (r.payment?.amount || 299);
        }, 0);

        const stats = {
            totalReports: reports.length,
            totalRevenue: totalRevenue,
            averageIncome: 0,
            averageExpenses: 0,
            averageSavingsRate: 0,
            healthScoreDistribution: {
                excellent: 0,
                good: 0,
                fair: 0,
                needsImprovement: 0,
                critical: 0
            },
            monthlySales: Array(12).fill(0), // J, F, M...
            weeklyReports: Array(7).fill(0) // M, T, W...
        };

        if (reports.length > 0) {
            const totalIncome = reports.reduce((sum, r) => sum + (r.salary?.totalIncome || 0), 0);
            const totalExpenses = reports.reduce((sum, r) => sum + (r.expenses?.totalExpenses || 0), 0);
            const totalSavingsRate = reports.reduce((sum, r) => sum + parseFloat(r.financialHealth?.savingsRate || 0), 0);

            stats.averageIncome = Math.round(totalIncome / reports.length);
            stats.averageExpenses = Math.round(totalExpenses / reports.length);
            stats.averageSavingsRate = (totalSavingsRate / reports.length).toFixed(1);

            // Process reports for trends
            reports.forEach(report => {
                const date = report.createdAt;
                if (date) {
                    // Weekly (Day of week 0-6: Sun-Sat)
                    const day = date.getDay();
                    const adjustedDay = day === 0 ? 6 : day - 1; // Map Sun-Sat to Mon-Sun (0-6)
                    stats.weeklyReports[adjustedDay]++;

                    // Monthly (0-11)
                    const month = date.getMonth();
                    stats.monthlySales[month]++;
                }

                const score = report.financialHealth?.score?.toLowerCase();
                if (score === 'excellent') stats.healthScoreDistribution.excellent++;
                else if (score === 'good') stats.healthScoreDistribution.good++;
                else if (score === 'fair') stats.healthScoreDistribution.fair++;
                else if (score === 'needs improvement') stats.healthScoreDistribution.needsImprovement++;
                else stats.healthScoreDistribution.critical++;
            });
        }

        return stats;
    } catch (error) {
        console.error('Error calculating statistics:', error);
        throw error;
    }
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Calculate financial health score
 * @param {number} surplus - Monthly surplus
 * @param {number} totalIncome - Total monthly income
 * @returns {string} - Health score
 */
const calculateHealthScore = (surplus, totalIncome) => {
    if (totalIncome === 0) return 'Critical';
    const savingsRate = (surplus / totalIncome) * 100;

    if (savingsRate >= 30) return 'Excellent';
    if (savingsRate >= 20) return 'Good';
    if (savingsRate >= 10) return 'Fair';
    if (savingsRate >= 0) return 'Needs Improvement';
    return 'Critical';
};

/**
 * Generate personalized recommendations
 * @param {Object} reportData - Report data
 * @returns {Array<string>} - Array of recommendations
 */
const generateRecommendations = (reportData) => {
    const recommendations = [];
    const { salary, expenses } = reportData;
    const savingsRate = (expenses.surplus / salary.totalIncome) * 100;

    if (expenses.surplus < 0) {
        recommendations.push('Urgent: Your expenses exceed income. Review and cut unnecessary expenses.');
    }

    if (savingsRate < 20) {
        recommendations.push('Increase your savings rate to at least 20% of income.');
    }

    if (expenses.housing > salary.totalIncome * 0.3) {
        recommendations.push('Housing costs are high (>30% of income). Consider alternatives.');
    }

    if (expenses.loanEMIs > salary.totalIncome * 0.4) {
        recommendations.push('Debt burden is high. Focus on debt reduction.');
    }

    if (savingsRate >= 30) {
        recommendations.push('Excellent savings rate! Consider diversifying investments.');
    }

    if (expenses.entertainment > salary.totalIncome * 0.1) {
        recommendations.push('Entertainment expenses are high. Consider reducing discretionary spending.');
    }

    if (expenses.savings < salary.totalIncome * 0.1) {
        recommendations.push('Increase your monthly savings allocation.');
    }

    return recommendations;
};

export default {
    saveDiagnosisReport,
    saveDraftReport,
    getIncompleteReports,
    subscribeIncompleteReports,
    getAllDiagnosisReports,
    getDiagnosisReportById,
    getLatestReportByEmail,
    updateReportStatus,
    deleteReport,
    getReportsByDateRange,
    getReportsStatistics
};
